import get from 'lodash/get';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { StoreApi, UseBoundStore } from 'zustand';

export const usePostHogSettingsSyncFromStore = <T extends object>(
  store: UseBoundStore<StoreApi<T>>,
  namespace: string,
  paths: string[],
) => {
  const posthog = usePostHog();

  // Use a ref to debounce rapid updates (e.g., sliders)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!posthog) return;

    // 1. SYNC ON LOAD (The "Status Quo")
    // Ensures we capture settings for users who never change them
    const initialState = store.getState();
    const initialProperties: Record<string, any> = {};

    paths.forEach((path) => {
      const value = get(initialState, path);
      // Format: "settings_stream_volume", "settings_ui_theme"
      initialProperties[`settings_${namespace}_${path.replace(/\./g, '_')}`] = value;
    });

    // Send the initial snapshot immediately
    posthog.people.set(initialProperties);

    // 2. SYNC ON CHANGE
    const unsub = store.subscribe((state, prevState) => {
      const updates: Record<string, any> = {};
      let hasChanges = false;

      paths.forEach((path) => {
        const value = get(state, path);
        const prevValue = get(prevState, path);

        if (value !== prevValue) {
          updates[`settings_${namespace}_${path.replace(/\./g, '_')}`] = value;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        // Debounce the update to avoid flooding PostHog API on sliders/inputs
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
          posthog.people.set(updates);
        }, 1000); // 1-second debounce
      }
    });

    return () => {
      unsub();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [posthog, store, namespace, JSON.stringify(paths)]);
};
